import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, Download, Plus, Search } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Avatar } from '../../components/Avatar';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { formatDate } from '../../lib/format';
import type { ResellerCustomer } from '../../mocks/reseller';
import { useResellerCustomers } from '../../api/reseller';
import { cn } from '../../lib/cn';
import { downloadCsv } from '../../lib/csv';

const STATUS_CHIPS: Array<{ id: ResellerCustomer['status'] | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'trial', label: 'Trial' },
  { id: 'past_due', label: 'Past due' },
  { id: 'suspended', label: 'Suspended' },
];

const PLAN_OPTIONS = ['All plans', 'Starter', 'Basic', 'Pro', 'Enterprise'];

/** Reseller customers (businesses) list with filter chips and search. */
export function Customers() {
  const { data: resellerCustomers = [] } = useResellerCustomers();
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_CHIPS)[number]['id']>('all');
  const [planFilter, setPlanFilter] = useState('All plans');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return resellerCustomers.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (planFilter !== 'All plans' && c.plan !== planFilter) return false;
      if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !c.ownerEmail.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [statusFilter, planFilter, search, resellerCustomers]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title={`Customers (${resellerCustomers.length})`}
        subtitle="Every business operating under your reseller account."
        action={
          <>
            <Button variant="secondary" size="sm" iconLeft={<Download className="w-4 h-4" />} onClick={() =>
              downloadCsv(
                `customers-${new Date().toISOString().slice(0, 10)}`,
                ['id', 'name', 'owner_email', 'plan', 'status', 'joined', 'mrr_minor'],
                filtered.map((c) => [c.id, c.name, c.ownerEmail, c.plan, c.status, c.joinedAt, c.mrrMinor]),
              )
            }>
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Link
              to="/reseller/customers/new"
              className="inline-flex items-center gap-2 rounded-md bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 px-4 py-2 text-sm font-medium hover:bg-brand-hover dark:hover:bg-brand-dm-hover transition"
            >
              <Plus className="w-4 h-4" />
              Create customer
            </Link>
          </>
        }
      />

      {/* Status chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => setStatusFilter(chip.id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition',
              statusFilter === chip.id
                ? 'bg-brand text-white border-brand dark:bg-brand-dm dark:text-zinc-950 dark:border-brand-dm'
                : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800',
            )}
          >
            {chip.label}
            {chip.id !== 'all' && (
              <span className="tabular-nums opacity-70">
                {resellerCustomers.filter((c) => c.status === chip.id).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search + plan filter */}
      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <div className="relative">
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="pl-3 pr-9 py-2 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm appearance-none"
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400 pointer-events-none" />
          </div>
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by name or email"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <EmptyState
            title="No customers match these filters"
            description="Try removing a chip or clearing your search."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setPlanFilter('All plans');
                  setSearch('');
                }}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="px-5 sm:px-6 py-3 w-8">
                    <input type="checkbox" className="rounded border-zinc-300 dark:border-zinc-600" />
                  </th>
                  <th className="text-left font-medium px-4 py-3">Name</th>
                  <th className="text-left font-medium px-4 py-3">Plan</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Joined</th>
                  <th className="text-right font-medium px-5 sm:px-6 py-3">MRR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-4">
                      <input type="checkbox" className="rounded border-zinc-300 dark:border-zinc-600" />
                    </td>
                    <td className="px-4 py-4">
                      <Link to="/reseller/customers/$id" params={{ id: c.id }} className="flex items-center gap-3 group">
                        <Avatar name={c.name} size="md" />
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-950 dark:text-zinc-50 truncate group-hover:underline">
                            {c.name}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{c.ownerEmail}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200">{c.plan}</td>
                    <td className="px-4 py-4">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums whitespace-nowrap">
                      {formatDate(c.joinedAt, { compact: true })}
                    </td>
                    <td className="px-5 sm:px-6 py-4 text-right">
                      <span className="inline-flex items-baseline gap-0.5">
                        <Money minor={c.mrrMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">/mo</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: ResellerCustomer['status'] }) {
  switch (status) {
    case 'active':
      return <Badge tone="success">Active</Badge>;
    case 'trial':
      return <Badge tone="warning">Trial</Badge>;
    case 'past_due':
      return <Badge tone="error">Past due</Badge>;
    case 'suspended':
      return <Badge tone="neutral">Suspended</Badge>;
    case 'canceled':
      return <Badge tone="neutral">Canceled</Badge>;
  }
}
